import { useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ArrowLeft, Send } from 'lucide-react';
import {
  aiAgentPortraitSrc,
  getAiAgentSettings,
} from '../../services/cabinet/aiAgentSettings';
import { useNovaAgentStore } from '../../store/useNovaAgentStore';
import { cn } from '../../lib/cn';

export function NovaConversation({
  onBack,
  hideBack = false,
}: {
  onBack?: () => void;
  hideBack?: boolean;
}) {
  const agent = useQuery({
    queryKey: ['cabinet', 'ai-agent'],
    queryFn: getAiAgentSettings,
    staleTime: 60_000,
  });
  const messages = useNovaAgentStore((s) => s.messages);
  const pending = useNovaAgentStore((s) => s.pending);
  const send = useNovaAgentStore((s) => s.send);
  const [draft, setDraft] = useState('');
  const endRef = useRef<HTMLDivElement>(null);
  const name = agent.data?.name || 'Nova Stellaris';
  const src = aiAgentPortraitSrc(agent.data?.photoUrl ?? null);

  useEffect(() => {
    endRef.current?.scrollIntoView({ block: 'end' });
  }, [messages.length]);

  function submit() {
    send(draft);
    setDraft('');
  }

  return (
    <div className="flex flex-col min-h-0 flex-1 bg-white">
      <header className="flex items-center gap-3 px-3 py-2 border-b border-neutral-200 shrink-0">
        {hideBack ? null : (
          <button
            type="button"
            onClick={onBack}
            className="p-1 rounded hover:bg-cime-primary/10 text-cime-text-muted"
            aria-label="Retour aux messages"
          >
            <ArrowLeft className="w-4 h-4" />
          </button>
        )}
        <img src={src} alt="" className="h-8 w-8 rounded-full object-cover" />
        <div className="min-w-0">
          <p className="text-sm font-semibold text-neutral-900 truncate">{name}</p>
          <p className="text-[11px] text-neutral-500">Agent du cabinet</p>
        </div>
      </header>
      <div className="flex-1 min-h-0 overflow-y-auto px-3 py-3 space-y-2">
        {messages.length === 0 ? (
          <p className="text-sm text-neutral-500">
            Écris à {name}. Une facture ou un devis ouvre la feuille, et le même brouillon part dans Polaris.
          </p>
        ) : (
          messages.map((m) => (
            <p
              key={m.id}
              className={cn(
                'max-w-[90%] rounded-lg px-3 py-2 text-sm whitespace-pre-wrap',
                m.role === 'user'
                  ? 'ml-auto bg-cime-primary text-white'
                  : 'bg-neutral-100 text-neutral-800',
              )}
            >
              {m.text}
            </p>
          ))
        )}
        {pending ? <p className="text-xs text-neutral-400">Nova écrit…</p> : null}
        <div ref={endRef} />
      </div>
      <form
        className="flex items-end gap-2 border-t border-neutral-200 px-3 py-2"
        onSubmit={(e) => {
          e.preventDefault();
          submit();
        }}
      >
        <textarea
          value={draft}
          rows={2}
          placeholder={`Écrire à ${name}…`}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              submit();
            }
          }}
          className="flex-1 resize-none rounded-lg border border-neutral-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-cime-primary"
        />
        <button
          type="submit"
          disabled={pending || !draft.trim()}
          className="h-10 w-10 shrink-0 rounded-full bg-cime-primary text-white disabled:opacity-40 flex items-center justify-center"
          aria-label="Envoyer"
        >
          <Send className="w-4 h-4" />
        </button>
      </form>
    </div>
  );
}
